/**
 * Single source of truth for the credential list shown to contractors.
 *
 * Each section is tagged with the industries it belongs to so a hospitality worker is not
 * shown healthcare credentials (and vice versa). 'General' sections are shown to everyone.
 *
 * Before this file the same lists were duplicated in CertificateListComponent and
 * CertificateService and rendered as hardcoded template blocks. Add/remove credentials here
 * only — the UI, the "x of y" counter and getAllCertificatesByName() all read from this.
 */

export type CredentialIndustry = 'Healthcare' | 'Hospitality' | 'General';

export interface ICredential {
    // Name as stored on the user's certificate object — must match the API exactly.
    name: string;
    // Optional label override for the UI.
    displayName?: string;
    // Filled in at runtime from the skills config (requiredCertificatesBySkill).
    isRequired?: boolean;
}

export interface ICredentialSection {
    key: string;
    // Used when there is no translation key for the heading.
    title: string;
    translateKey?: string;
    industries: CredentialIndustry[];
    credentials: ICredential[];
}

/**
 * Vulnerable Sector Check is deliberately NOT part of the catalog sections: it has its own
 * heading, "Or" separator and request-letter link in the template, and is gated on
 * isVSCRequired rather than on industry. It is still a credential, so it is listed here for
 * callers that need every credential name.
 */
export const VSC_CREDENTIALS: ICredential[] = [
    { name: 'Vulnerable Sector Check' },
    { name: 'Receipt of Vulnerable Sector Check' }
];

export const CREDENTIAL_CATALOG: ICredentialSection[] = [
    {
        key: 'covid',
        title: 'COVID credential',
        translateKey: 'PROFILE.COVID credential',
        // Shown to every contractor; only healthcare workers see it flagged as required.
        industries: ['General'],
        credentials: [
            { name: 'COVID-19 Vaccination' }
        ]
    },
    {
        key: 'general',
        title: 'General credentials',
        industries: ['General'],
        credentials: [
            { name: 'Government Issued Photo ID' },
            { name: 'Proof of Eligibility to work in Canada' },
            { name: 'Driver’s License' },
            { name: 'RCMP Background Check' },
            { name: 'Other', displayName: 'Additional Credential' }
        ]
    },
    {
        key: 'healthcare',
        title: 'Healthcare Credentials',
        translateKey: 'PROFILE.Healthcare Credentials',
        industries: ['Healthcare'],
        credentials: [
            { name: 'CMRTO Registration' },
            { name: 'CPR' },
            { name: 'Current Flu Vaccination' },
            { name: 'Immunization Record' },
            { name: 'Nursing License/Certification', displayName: 'Nursing License / Certification' },
            { name: 'CNO Registration', displayName: 'CNO Registration' },
            { name: 'Medical Lab Certificate' },
            { name: 'N95' },
            { name: 'Nursing Specialization' },
            { name: 'TB Testing' },
            { name: 'Professional Liability Protection' },
            { name: 'CPO Registration' },
            { name: 'COTO Registration' },
            { name: 'Diploma/Certification' },
            { name: 'BCLS Certification' },
            { name: 'CAMRT Certification' }
        ]
    },
    {
        key: 'hospitality',
        title: 'Other',
        industries: ['Hospitality'],
        credentials: [
            { name: 'Food Handler Certified' },
            { name: 'Smart Serve Certified' }
        ]
    },
    {
        key: 'dietaryAide',
        title: 'Dietary aide credentials',
        translateKey: 'PROFILE.Dietary aide credentials',
        industries: ['Healthcare'],
        credentials: [
            { name: 'Food Service/Safe Certification', displayName: 'Food Service / Safe Certification' }
        ]
    },
    {
        key: 'psw',
        title: 'PSW/UCP credentials',
        industries: ['Healthcare'],
        credentials: [
            { name: 'CPI' },
            { name: 'PSW Certificate/Documentation', displayName: 'PSW Certificate / Documentation' },
            { name: 'HCA Certification', displayName: 'HCA Certification' },
            { name: 'OPSWA Membership', displayName: 'OPSWA' },
            { name: 'HSCPOA Registration' }
        ]
    }
];

/**
 * Does this section belong to a contractor working in these industries?
 * 'General' sections are shown to everyone.
 */
export function isSectionForIndustries(section: ICredentialSection, isHCW: boolean, isHospitality: boolean): boolean {
    return section.industries.some((industry) => {
        if (industry === 'General') { return true; }
        if (industry === 'Healthcare') { return !!isHCW; }
        return !!isHospitality;
    });
}

/** A fresh copy of the catalog — callers tag credentials with isRequired, so never hand out the original. */
export function cloneCredentialCatalog(): ICredentialSection[] {
    return CREDENTIAL_CATALOG.map(section => ({
        ...section,
        credentials: section.credentials.map(credential => ({ ...credential }))
    }));
}

/** Credential names a contractor in these industries is shown, used for the "x of y" counter. */
export function credentialNamesForIndustries(isHCW: boolean, isHospitality: boolean): string[] {
    return CREDENTIAL_CATALOG
        .filter(section => isSectionForIndustries(section, isHCW, isHospitality))
        .reduce((names, section) => names.concat(section.credentials.map(c => c.name)), [] as string[]);
}

/** Every credential name, regardless of industry, including the Vulnerable Sector Check pair. */
export function allCredentialNames(): string[] {
    return CREDENTIAL_CATALOG
        .reduce((names, section) => names.concat(section.credentials.map(c => c.name)), [] as string[])
        .concat(VSC_CREDENTIALS.map(c => c.name));
}
